/**
 * HighlightedText — renders an AI message body with selected phrases turned
 * into tappable, accent-coloured spans. Tapping a highlight sends the
 * matching `reply` as the user's next message.
 *
 * Backend contract: the AI agent attaches `agape_highlights:
 * Array<{text, reply}>` to a Stream Chat message. This component is rendered
 * in place of Stream's default text body when that array is present (the
 * default body is hidden via `.agape-has-highlights .str-chat__message-text`
 * in `public/stream-chat-overrides.css`). Mutually exclusive with
 * `quick_replies` — the XOR is enforced by `QuickReplyButtons`.
 *
 * Native/web safe: only `<View>` and `<Text>` from react-native; tap is
 * handled via `onPress` on a nested `<Text>` (a wrapping `<Pressable>`
 * collapses to a block element on web and breaks inline body flow).
 * Highlights are coloured with `colors.solar` — NO `textDecorationLine` so
 * they read as soft accents rather than underlined links.
 */
import React, { useCallback, useMemo } from 'react';
import { Platform, StyleProp, Text, TextStyle, View } from 'react-native';
import { colors } from '../../styles/colors';
import { ACCENT_TEXT } from '../../styles/accentText';
import { useMessagesSurface } from './messagesSurface';

export interface AgapeHighlight {
    /** Exact substring of the message body to make tappable. */
    text: string;
    /** User message dispatched when the highlight is tapped. */
    reply: string;
}

interface HighlightedTextProps {
    text: string;
    highlights: AgapeHighlight[];
    /**
     * Stream Chat channel object. Typed `any` to match neighbouring chat
     * components — stream-chat's `Channel` type isn't re-exported through a
     * shared alias here. Used for `.sendMessage({ text: highlight.reply })`.
     * Optional now: the backend-agnostic thread passes {@link onReply} instead.
     */
    channel?: any;
    /**
     * CORE-396 — backend-agnostic reply dispatch. When set, a highlight tap
     * calls this with the reply text (routed through the active
     * {@link ChatThreadSource}) instead of `channel.sendMessage`. Takes
     * precedence over `channel`.
     */
    onReply?: (reply: string) => void;
    /**
     * CORE-396 — render just the inline segments without the component's own
     * bubble surface, for hosts (AgapellaChatThread) that already provide the
     * bubble. Default false keeps the standalone-bubble behaviour Stream needs.
     */
    bare?: boolean;
    /**
     * CORE-705 — override the body colour/typography. {@link THEMED_TEXT_STYLE}
     * resolves to `colors.textMain` (near-black) off web, which is invisible on
     * the native thread's dark artwork. Hosts pass their own foreground here;
     * applied last so it wins over the themed default.
     */
    textStyle?: StyleProp<TextStyle>;
    messageId?: string;
    /**
     * Stream user id of the AI companion that produced the message (e.g.
     * `ai_buddha`). Forwarded as `quick_reply_target` so the backend's
     * multi-avatar router short-circuits the LLM re-classification step
     * and addresses the response back to the same companion. Mirrors the
     * hint that `<QuickReplies>` already sends — without it, taps on
     * inline highlights routinely re-route to a different companion.
     */
    quickReplyTarget?: string;
}

export type Segment =
    | { kind: 'plain'; text: string }
    | { kind: 'highlight'; text: string; reply: string };

/** Inline run inside a plain segment: regular text or markdown-bold emphasis. */
export type BoldRun = { bold: boolean; text: string };

/**
 * Style applied to tappable highlight spans. Exported so unit tests can
 * pin the visual contract (color + bold) without rendering the component.
 */
export const HIGHLIGHT_STYLE = {
    color: colors.solar,
    fontWeight: '700' as const,
};

/**
 * Style applied to markdown-bold runs inside plain segments. Exported so
 * unit tests can pin the visual contract.
 */
export const BOLD_RUN_STYLE = {
    fontWeight: '700' as const,
};

/**
 * Bubble container styling for the custom highlighted-text body. When
 * `agape_highlights` is present, `public/stream-chat-overrides.css` hides
 * the default Stream Chat `.str-chat__message-text` and this component is
 * mounted as a sibling inside the message wrapper. Without its own bubble
 * surface the text appears bare in the feed — the user reported "сообщение
 * не как сообщение, а просто текст без бабла". These tokens mirror Stream
 * Chat's default AI bubble (warm surface, 12/16 padding, 16 radius) so the
 * highlighted variant is visually indistinguishable except for the orange
 * tappable phrases inside.
 */
export const BUBBLE_STYLE = {
    backgroundColor: colors.surfaceWarm,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignSelf: 'flex-start' as const,
    maxWidth: 560,
};

export const THEMED_BUBBLE_STYLE = {
    backgroundColor: Platform.OS === 'web' ? 'var(--ag-surface-warm, #FAFAF8)' : colors.surfaceWarm,
};

export const THEMED_TEXT_STYLE = {
    color: Platform.OS === 'web' ? 'var(--ag-text-primary, #2B2730)' : colors.textMain,
};

const BOLD_RE = /\*\*([^*\n]+?)\*\*/g;

/**
 * Split a plain segment into alternating regular/bold runs by extracting
 * ``**…**`` markdown spans. The backend's autoconvert heuristic strips
 * stars ONLY from the sentence it converted to highlights (see
 * `chat_service.py:_autoconvert_inprose_bold`); any other emphasis bold
 * the LLM produced reaches the frontend with literal `**` markers. Stream
 * Chat's default renderer parses them — but `<HighlightedText>` is rendered
 * *in place of* that default body when `agape_highlights` is present, so
 * we have to parse the same syntax here, otherwise users see raw stars.
 *
 * Unbalanced `**` (no closing pair on the same line) is preserved verbatim.
 */
export function splitBoldRuns(text: string): BoldRun[] {
    if (!text) return [];
    const runs: BoldRun[] = [];
    let cursor = 0;
    BOLD_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = BOLD_RE.exec(text)) !== null) {
        if (m.index > cursor) {
            runs.push({ bold: false, text: text.slice(cursor, m.index) });
        }
        runs.push({ bold: true, text: m[1] });
        cursor = m.index + m[0].length;
    }
    if (cursor < text.length) {
        runs.push({ bold: false, text: text.slice(cursor) });
    }
    if (runs.length === 0) {
        runs.push({ bold: false, text });
    }
    return runs;
}

/**
 * Walk the body text once and split it into plain / highlight segments.
 * Longer phrases are tried first so nested matches resolve deterministically;
 * once a span is claimed it can't be re-claimed by another highlight.
 * Phrases that don't appear in the body are silently skipped.
 */
export function buildSegments(text: string, highlights: AgapeHighlight[]): Segment[] {
    if (!text) return [];

    const valid = (highlights || [])
        .filter(
            (h) =>
                h &&
                typeof h.text === 'string' &&
                h.text.length > 0 &&
                typeof h.reply === 'string',
        )
        .sort((a, b) => b.text.length - a.text.length);

    if (valid.length === 0) {
        return [{ kind: 'plain', text }];
    }

    const claims: Array<{ start: number; end: number; reply: string }> = [];
    for (const h of valid) {
        let from = 0;
        while (from < text.length) {
            const idx = text.indexOf(h.text, from);
            if (idx === -1) break;
            const end = idx + h.text.length;
            const overlaps = claims.some((c) => idx < c.end && end > c.start);
            if (!overlaps) {
                claims.push({ start: idx, end, reply: h.reply });
            }
            from = end;
        }
    }

    if (claims.length === 0) {
        return [{ kind: 'plain', text }];
    }

    claims.sort((a, b) => a.start - b.start);

    const segments: Segment[] = [];
    let cursor = 0;
    for (const c of claims) {
        if (c.start > cursor) {
            segments.push({ kind: 'plain', text: text.slice(cursor, c.start) });
        }
        segments.push({ kind: 'highlight', text: text.slice(c.start, c.end), reply: c.reply });
        cursor = c.end;
    }
    if (cursor < text.length) {
        segments.push({ kind: 'plain', text: text.slice(cursor) });
    }
    return segments;
}

/**
 * Build the Stream Chat sendMessage payload for a highlight tap. Mirrors
 * the structure that {@link QuickReplies} attaches when a chip is pressed
 * so the backend treats both interactions identically:
 *  - `quoted_message_id` lets the backend route the reply back to the
 *    specific AI message that offered the choice (multi-avatar routing).
 *  - `quick_reply_target` short-circuits the LLM router to address the
 *    response to the same companion that produced the highlights —
 *    forwarded only when it looks like a bot id (`ai_*` prefix); a plain
 *    user id is dropped to avoid polluting the routing branch with
 *    meaningless data.
 *
 * Pure function so unit tests can pin the payload contract without
 * spinning up a Stream Chat mock.
 */
export interface HighlightPressOptions {
    messageId?: string;
    quickReplyTarget?: string;
}

export function buildHighlightPressPayload(
    reply: string,
    opts: HighlightPressOptions,
): Record<string, any> {
    const payload: Record<string, any> = { text: reply };
    if (opts.messageId) payload.quoted_message_id = opts.messageId;
    const target = opts.quickReplyTarget;
    if (typeof target === 'string' && target.startsWith('ai_')) {
        payload.quick_reply_target = target;
    }
    return payload;
}

export function HighlightedText({
    text,
    highlights,
    channel,
    onReply,
    bare = false,
    textStyle,
    messageId,
    quickReplyTarget,
}: HighlightedTextProps) {
    const segments = useMemo(() => buildSegments(text || '', highlights || []), [text, highlights]);
    const inMessages = !!useMessagesSurface() && Platform.OS === 'web';

    const handlePress = useCallback(
        (reply: string) => {
            if (!reply) return;
            // Backend-agnostic path wins: send through the thread source.
            if (onReply) {
                onReply(reply);
                return;
            }
            if (!channel) return;
            try {
                channel.sendMessage(
                    buildHighlightPressPayload(reply, { messageId, quickReplyTarget }),
                );
            } catch (err) {
                if (__DEV__) {

                    console.warn('[HighlightedText] sendMessage failed', err);
                }
            }
        },
        [channel, onReply, messageId, quickReplyTarget],
    );

    if (segments.length === 0) return null;

    const body = (
        <Text className="agape-highlighted-text-body" style={[THEMED_TEXT_STYLE, { lineHeight: 22 }, textStyle]}>
                {segments.map((seg, idx) => {
                    const key = `${messageId || 'm'}-${idx}`;
                    if (seg.kind === 'plain') {
                        const runs = splitBoldRuns(seg.text);
                        return (
                            <Text key={`p-${key}`}>
                                {runs.map((run, rIdx) => (
                                    <Text
                                        key={`p-${key}-r${rIdx}`}
                                        style={run.bold ? BOLD_RUN_STYLE : undefined}
                                    >
                                        {run.text}
                                    </Text>
                                ))}
                            </Text>
                        );
                    }
                    // Tappable highlight — coloured accent, NO underline.
                    // `onPress` on a nested <Text> keeps inline flow on web
                    // and native; a wrapping <Pressable> would break to a
                    // block element on web and disrupt the body layout.
                    return (
                        <Text
                            key={`h-${key}`}
                            onPress={() => handlePress(seg.reply)}
                            accessibilityRole="button"
                            accessibilityHint={seg.reply}
                            testID="agape-highlight"
                            // CORE-1791 (2b) — в разделе сообщений акцентный текст — общий
                            // токен (#E98245 на пузыре v3 давал 2.2–2.5:1).
                            style={inMessages ? [HIGHLIGHT_STYLE, { color: ACCENT_TEXT }] : HIGHLIGHT_STYLE}
                        >
                            {seg.text}
                        </Text>
                    );
                })}
        </Text>
    );

    // Host already draws the bubble (AgapellaChatThread) → emit inline segments only.
    if (bare) return body;

    return (
        <View
            className="agape-highlighted-text-bubble"
            testID="agape-highlighted-text"
            style={[BUBBLE_STYLE, THEMED_BUBBLE_STYLE, { flexDirection: 'row', flexWrap: 'wrap' }]}
        >
            {body}
        </View>
    );
}

export default HighlightedText;
